import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

/**
 * PWA Install Prompt banner.
 * - Chrome/Android/Edge: intercept `beforeinstallprompt` and show a native install button.
 * - iOS Safari: show manual instructions (Share → Add to Home Screen).
 * - Hidden if already installed (display-mode: standalone) or dismissed by user.
 * - Reflows above the CookieBanner while cookie consent is pending, so its
 *   action buttons never get hidden on small screens (iPhone 16 / Pro Max).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [visible, setVisible] = useState(false);
  const [cookieVisible, setCookieVisible] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Initial cookie banner state from localStorage
    setCookieVisible(!localStorage.getItem("fife_cookie_consent"));
    const onCookieEvt = (e) => setCookieVisible(!!e.detail?.visible);
    window.addEventListener("fife:cookie-visible", onCookieEvt);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const ua = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
    const isMobileViewport = window.innerWidth <= 900;

    // Fallback: if the browser never fires beforeinstallprompt (iOS Safari,
    // in-app browsers, Firefox Android, etc.) or we are on a mobile viewport
    // → show the banner with manual instructions.
    const t = setTimeout(() => {
      if (isIos && isSafari) setShowIos(true);
      if (isIos || isMobileViewport) setVisible(true);
    }, 1200);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("fife:cookie-visible", onCookieEvt);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") setVisible(false);
      setDeferred(null);
    } catch {}
  };

  const dismiss = () => {
    localStorage.setItem("fife_pwa_dismissed_at", String(Date.now()));
    setVisible(false);
  };

  if (!visible || cookieVisible) return null;

  // Position at bottom on mobile, right on desktop.
  const mobileBottom = "bottom-3";

  return (
    <div role="region" aria-live="polite" aria-label="Installa FIFE come app"
         className={`fixed z-[80] left-2 right-2 ${mobileBottom} md:left-auto md:right-4 md:bottom-4 md:max-w-sm transition-all duration-300 fade-up`}
         style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
         data-testid="pwa-install-banner">
      <div className="rounded-2xl bg-slate-900 text-white shadow-2xl border border-white/10 p-4 flex gap-3 items-start">
        <div className="w-16 h-16 rounded-xl bg-white shadow-inner flex items-center justify-center shrink-0 ring-1 ring-black/10">
          <img src="/logo-fife-192.png" alt="FIFE" className="w-16 h-16 object-contain aspect-square" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="display font-black text-sm">Installa FIFE come app</div>
          {showIos ? (
            <div className="text-white/80 text-xs mt-1 leading-relaxed">
              Tocca <Share className="inline w-3.5 h-3.5 mx-0.5" /> "Condividi" nella barra di Safari, poi <b>«Aggiungi alla schermata Home»</b>.
            </div>
          ) : deferred ? (
            <div className="text-white/80 text-xs mt-1 leading-relaxed">
              Apri il Fantacalcio come un'app dallo schermo: più rapido, offline shell, notifiche pronte.
            </div>
          ) : (
            <div className="text-white/80 text-xs mt-1 leading-relaxed">
              Sul tuo telefono usa il menu del browser (⋮ o Condividi) e scegli <b>«Aggiungi alla schermata Home»</b> per aprire FIFE come app.
            </div>
          )}
          {!showIos && deferred && (
            <button onClick={install}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-xs font-black uppercase tracking-widest bg-white text-slate-900 hover:bg-slate-100"
                    data-testid="pwa-install-btn">
              <Download className="w-3.5 h-3.5" /> Installa
            </button>
          )}
        </div>
        <button onClick={dismiss} className="p-1.5 rounded-full hover:bg-white/10 text-white/70 shrink-0" data-testid="pwa-install-close" aria-label="Chiudi">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
